// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";
import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Form, Row, Col } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import DateInput from "../../common/DateInput";
import RichTextEditor from "../../common/RichTextEditor";
import { htmlForApi } from "../../../utils/richText";


// API endpoint
const ADD_TAXATION_PROJECT = "https://ultima.icsgr.com/api/data_management/taxation_projects/";

function AddTaxationProjectModal({
  refreshData,
  onTaxationProjectCreated,
  defaultConsultantId,
  lockConsultant = false,
}) {
  const [show, setShow] = useState(false);

  const [client_id, setClientId] = useState("");
  const [consultant_id, setConsultantId] = useState("");
  const [taxuse, setTaxuse] = useState("");
  const [deadline, setDeadline] = useState("");
  const [declaredone, setDeclaredone] = useState(false);
  const [declarationdate, setDeclarationdate] = useState("");
  const [comment, setComment] = useState("");

  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);

  const resetForm = () => {
    setClientId("");
    setConsultantId("");
    setTaxuse("");
    setDeadline("");
    setDeclaredone(false);
    setDeclarationdate("");
    setComment("");
  };

  const handleClose = () => setShow(false);
  const handleShow = () => {
    resetForm();
    if (defaultConsultantId) setConsultantId(defaultConsultantId);
    setShow(true);
  };

  useEffect(() => {
    if (show) {
      loadRefs();
    }
  }, [show]);

  const loadRefs = async () => {
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const [clientsRes, consultantsRes] = await Promise.all([
        axios.get("https://ultima.icsgr.com/api/data_management/all_clients/", { headers: currentHeaders }),
        axios.get("https://ultima.icsgr.com/api/administration/all_consultants/", { headers: currentHeaders }),
      ]);
      setClients(clientsRes?.data?.all_clients || []);
      setConsultants(consultantsRes?.data?.all_consultants || []);
    } catch (e) {
      console.error("Failed to load refs", e);
      setClients([]);
      setConsultants([]);
    }
  };

  const isClientValid = client_id !== "";
  const isConsultantValid = consultant_id !== "";
  const isTaxuseValid = /^\d{1,4}$/.test(taxuse.trim());
  const isFormValid = isClientValid && isConsultantValid && isTaxuseValid;

  const handleSubmit = async () => {
    if (!isFormValid) return;
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      await axios.post(ADD_TAXATION_PROJECT, {
        client_id,
        consultant_id,
        taxuse: taxuse.trim(),
        deadline: deadline || null,
        declaredone,
        declarationdate: declarationdate || null,
        comment: htmlForApi(comment),
      }, { headers: currentHeaders });

      Swal.fire({ icon: "success", title: "Success", text: "Taxation Project created successfully!" });
      setShow(false);
      const cb = refreshData || onTaxationProjectCreated;
      if (cb) cb();
    } catch (e) {
      console.error("Create taxation project error", e);
      const apiMsg = e?.response?.data?.error || e?.response?.data?.detail || "Failed to create taxation project";
      Swal.fire({ icon: "error", title: "Error", text: typeof apiMsg === 'object' ? JSON.stringify(apiMsg) : apiMsg });
    }
  };

  return (
    <>
      <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
        <BiPlus style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }} />
        Create new Taxation Project
      </Button>

      <GuardedModal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create new Taxation Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tax use *</Form.Label>
                <Form.Control value={taxuse} maxLength={40} onChange={(e) => setTaxuse(e.target.value)} placeholder="e.g., Income" />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <SelectWithInlineCreate
                className="mb-3"
                label="Client *"
                required
                value={client_id}
                onChange={setClientId}
                placeholderOption="Select client"
                overviewLinkEntity="client"
              >
                {clients.map((c) => (
                  <option key={c.client_id} value={c.client_id} title={c.client_id}>
                    {c.fullname || `${c.surname || ""} ${c.name || ""}`.trim() || c.client_id}
                  </option>
                ))}
              </SelectWithInlineCreate>
            </Col>
            <Col md={6}>
              <SelectWithInlineCreate
                className="mb-3"
                label="Consultant *"
                required
                value={consultant_id}
                onChange={setConsultantId}
                disabled={lockConsultant}
                placeholderOption="Select consultant"
                overviewLinkEntity="consultant"
              >
                {consultants.map((u) => (
                  <option key={u.consultant_id} value={u.consultant_id} title={u.consultant_id}>
                    {u.fullname || u.username || u.consultant_id}
                  </option>
                ))}
              </SelectWithInlineCreate>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Deadline</Form.Label>
                <DateInput value={deadline} onChange={(e) => setDeadline(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check type="checkbox" label="Declared" checked={declaredone} onChange={(e) => setDeclaredone(e.target.checked)} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Declaration date</Form.Label>
                <DateInput value={declarationdate} onChange={(e) => setDeclarationdate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Comment</Form.Label>
                <RichTextEditor rows={3} value={comment} onChange={setComment} placeholder="Optional" />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "red" }}>
                {!isClientValid && (<li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Client is required.</li>)}
                {!isConsultantValid && (<li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Consultant is required.</li>)}
                {!isTaxuseValid && (<li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} /> Tax use is required (4 digits).</li>)}
              </ul>
            ) : (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "green" }}>
                <li><AiOutlineCheckCircle style={{ fontSize: 18, marginRight: 6 }} /> Validated</li>
              </ul>
            )}
          </small>
          <Button color="red" onClick={handleClose}>Close</Button>
          <Button color="green" onClick={handleSubmit} disabled={!isFormValid}>Save Changes</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export default AddTaxationProjectModal;
